(() => {
  "use strict";

  const root = document.documentElement;

  const state = {
    pointerX: window.innerWidth / 2,
    pointerY: window.innerHeight / 2,
    targetPointerX: window.innerWidth / 2,
    targetPointerY: window.innerHeight / 2,
    scrollProgress: 0,
    isVisible: !document.hidden,
    isTouch:
      window.matchMedia("(hover: none)").matches ||
      window.matchMedia("(pointer: coarse)").matches,
    reducedMotion: window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches,
    frameRequested: false
  };

  const selectors = {
    reveal: "[data-nbr-reveal]",
    section: "[data-nbr-section]",
    vvideo: "[data-nbr-video-wrapper] video"
  };

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function detectQualityMode() {
    const savedMode = localStorage.getItem("nbr-quality");

    if (
      savedMode === "ultra" ||
      savedMode === "balanced" ||
      savedMode === "performance"
    ) {
      root.dataset.nbrQuality = savedMode;
      return;
    }

    const memory = navigator.deviceMemory || 4;
    const cores = navigator.hardwareConcurrency || 4;
    const narrowScreen = window.innerWidth < 760;

    if (
      state.reducedMotion ||
      memory <= 2 ||
      cores <= 2
    ) {
      root.dataset.nbrQuality = "performance";
      return;
    }

    if (
      state.isTouch ||
      narrowScreen ||
      memory <= 4 ||
      cores <= 4
    ) {
      root.dataset.nbrQuality = "balanced";
      return;
    }

    root.dataset.nbrQuality = "ultra";
  }

  function updateDeviceAttributes() {
    root.dataset.nbrTouch = state.isTouch ? "true" : "false";
    root.dataset.nbrMotion =
      state.reducedMotion ? "reduced" : "full";
  }

  function handlePointerMove(event) {
    if (
      state.isTouch ||
      state.reducedMotion ||
      !state.isVisible
    ) {
      return;
    }

    state.targetPointerX = event.clientX;
    state.targetPointerY = event.clientY;

    requestFrame();
  }

  function handleScroll() {
    const maximumScroll =
      document.documentElement.scrollHeight -
      window.innerHeight;

    state.scrollProgress =
      maximumScroll > 0
        ? clamp(window.scrollY / maximumScroll, 0, 1)
        : 0;

    root.style.setProperty(
      "--nbr-scroll-progress",
      state.scrollProgress.toFixed(4)
    );

    requestFrame();
  }

  function updatePointerVariables() {
    state.pointerX +=
      (state.targetPointerX - state.pointerX) * 0.12;

    state.pointerY +=
      (state.targetPointerY - state.pointerY) * 0.12;

    const percentageX =
      clamp(state.pointerX / window.innerWidth, 0, 1) * 100;

    const percentageY =
      clamp(state.pointerY / window.innerHeight, 0, 1) * 100;

    root.style.setProperty(
      "--nbr-pointer-x",
      `${percentageX.toFixed(2)}%`
    );

    root.style.setProperty(
      "--nbr-pointer-y",
      `${percentageY.toFixed(2)}%`
    );

    root.style.setProperty(
      "--nbr-pointer-normalized-x",
      ((percentageX - 50) / 50).toFixed(4)
    );

    root.style.setProperty(
      "--nbr-pointer-normalized-y",
      ((percentageY - 50) / 50).toFixed(4)
    );
  }

  function renderFrame() {
    state.frameRequested = false;

    if (
      !state.isVisible ||
      state.isTouch ||
      state.reducedMotion
    ) {
      return;
    }

    updatePointerVariables();

    const stillMoving =
      Math.abs(state.targetPointerX - state.pointerX) > 0.2 ||
      Math.abs(state.targetPointerY - state.pointerY) > 0.2;

    if (stillMoving) {
      requestFrame();
    }
  }

  function requestFrame() {
    if (state.frameRequested) return;

    state.frameRequested = true;
    requestAnimationFrame(renderFrame);
  }

  function initialiseRevealObserver(container = document) {
    const elements = container.querySelectorAll(
      `${selectors.reveal}:not([data-nbr-observed])`
    );

    if (!elements.length) return;

    if (
      state.reducedMotion ||
      !("IntersectionObserver" in window)
    ) {
      elements.forEach((element) => {
        element.dataset.nbrObserved = "true";
        element.classList.add("is-visible");
      });

      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add("is-visible");
          currentObserver.unobserve(entry.target);
        });
      },
      {
        threshold: 0.14,
        rootMargin: "0px 0px -8% 0px"
      }
    );

    elements.forEach((element) => {
      element.dataset.nbrObserved = "true";
      observer.observe(element);
    });
  }

  function initialiseSectionObserver(container = document) {
    const sections = container.querySelectorAll(
      `${selectors.section}:not([data-nbr-section-observed])`
    );

    if (!sections.length) return;

    if (!("IntersectionObserver" in window)) {
      sections.forEach((section) => {
        section.dataset.nbrSectionObserved = "true";
        section.classList.add("is-in-view");
      });

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle(
            "is-in-view",
            entry.isIntersecting
          );

          entry.target.dataset.nbrVisible =
            entry.isIntersecting ? "true" : "false";

          entry.target.dispatchEvent(
            new CustomEvent("nbr:visibility", {
              detail: {
                visible: entry.isIntersecting,
                ratio: entry.intersectionRatio
              }
            })
          );
        });
      },
      {
        threshold: [0, 0.08, 0.25, 0.5]
      }
    );

    sections.forEach((section) => {
      section.dataset.nbrSectionObserved = "true";
      observer.observe(section);
    });
  }

  function initialiseVideoObserver(container = document) {
    const videos = container.querySelectorAll(
      `${selectors.video}:not([data-nbr-video-observed])`
    );

    if (!videos.length) return;

    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;

          if (
            entry.isIntersecting &&
            state.isVisible &&
            video.autoplay
          ) {
            const playPromise = video.play();

            if (playPromise?.catch) {
              playPromise.catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: "150px 0px"
      }
    );

    videos.forEach((video) => {
      video.dataset.nbrVideoObserved = "true";
      observer.observe(video);
    });
  }

  function handleVisibilityChange() {
    state.isVisible = !document.hidden;

    root.dataset.nbrPageVisible =
      state.isVisible ? "true" : "false";

    document
      .querySelectorAll(selectors.video)
      .forEach((video) => {
        if (!state.isVisible) {
          video.pause();
        }
      });

    if (state.isVisible) {
      requestFrame();
    }
  }

  function initialise(container = document) {
    initialiseRevealObserver(container);
    initialiseSectionObserver(container);
    initialiseVideoObserver(container);
  }

  function exposeEngineControls() {
    window.NBR = window.NBR || {};

    window.NBR.setQuality = (mode) => {
      if (
        mode !== "ultra" &&
        mode !== "balanced" &&
        mode !== "performance"
      ) {
        return;
      }

      root.dataset.nbrQuality = mode;
      localStorage.setItem("nbr-quality", mode);

      window.dispatchEvent(
        new CustomEvent("nbr:qualitychange", {
          detail: { quality: mode }
        })
      );
    };

    window.NBR.resetQuality = () => {
      localStorage.removeItem("nbr-quality");
      detectQualityMode();
    };

    window.NBR.getQuality = () =>
      root.dataset.nbrQuality || "balanced";
  }

  detectQualityMode();
  updateDeviceAttributes();
  exposeEngineControls();
  initialise();
  handleScroll();

  window.addEventListener(
    "pointermove",
    handlePointerMove,
    { passive: true }
  );

  window.addEventListener(
    "scroll",
    handleScroll,
    { passive: true }
  );

  window.addEventListener(
    "resize",
    () => {
      state.isTouch =
        window.matchMedia("(hover: none)").matches ||
        window.matchMedia("(pointer: coarse)").matches;

      updateDeviceAttributes();
      detectQualityMode();
      handleScroll();
    },
    { passive: true }
  );

  document.addEventListener(
    "visibilitychange",
    handleVisibilityChange
  );

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initialise(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:reorder",
    () => {
      handleScroll();
    }
  );
})();